import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Table,
  Card,
  Select,
  Space,
  Spin,
  Tag,
  message,
  Empty,
  Button,
} from 'antd';
import {
  HistoryOutlined,
  EnvironmentOutlined,
  AppstoreOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useResponsive } from '@/shared/hooks/useResponsive';
import { siteApiService } from '@/features/sites/services/siteApiService';
import type {
  Site,
  Panel,
  PanelSiteHistoryItem,
  SitePanelHistoryItem,
} from '@/features/sites/types/site.types';

const { Title, Text } = Typography;

/** 日時フォーマット */
const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** CSVエスケープ */
const escapeCsvValue = (value: string | null | undefined): string => {
  if (value === null || value === undefined) {
    return '';
  }
  const strValue = String(value);
  if (strValue.includes(',') || strValue.includes('"') || strValue.includes('\n')) {
    return `"${strValue.replace(/"/g, '""')}"`;
  }
  return strValue;
};

/** CSVダウンロード */
const downloadCsv = (content: string, filename: string): void => {
  const bom = '\uFEFF';
  const blob = new Blob([bom + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/** 表示モード */
type ViewMode = 'panel' | 'site';

export const ArchivedDataPage: React.FC = () => {
  const { isMobile } = useResponsive();

  // 状態管理
  const [viewMode, setViewMode] = useState<ViewMode>('panel');
  const [sites, setSites] = useState<readonly Site[]>([]);
  const [panels, setPanels] = useState<readonly Panel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  // 盤モード
  const [selectedPanelId, setSelectedPanelId] = useState<number | null>(null);
  const [panelHistory, setPanelHistory] = useState<readonly PanelSiteHistoryItem[]>([]);
  const [panelInfo, setPanelInfo] = useState<{
    readonly panelName: string;
    readonly currentSite: { readonly id: number; readonly siteName: string };
  } | null>(null);

  // 現場モード
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [siteHistory, setSiteHistory] = useState<readonly SitePanelHistoryItem[]>([]);
  const [siteInfo, setSiteInfo] = useState<{
    readonly siteName: string;
    readonly currentPanels: readonly { readonly id: number; readonly panelName: string }[];
  } | null>(null);

  // 初期データ取得
  useEffect(() => {
    const loadData = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const [sitesData, panelsData] = await Promise.all([
          siteApiService.getAllSites(),
          siteApiService.getAllPanels(),
        ]);
        setSites(sitesData);
        setPanels(panelsData);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : '不明なエラー';
        message.error(`データの取得に失敗しました: ${errorMessage}`);
      } finally {
        setIsLoading(false);
      }
    };
    void loadData();
  }, []);

  // 盤履歴取得
  const loadPanelHistory = useCallback(async (panelId: number): Promise<void> => {
    setIsHistoryLoading(true);
    setPanelHistory([]);
    setPanelInfo(null);
    try {
      const result = await siteApiService.getPanelSiteHistory({ panelId });
      setPanelHistory(result.history);
      setPanelInfo({
        panelName: result.panelName,
        currentSite: result.currentSite,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '不明なエラー';
      message.error(`履歴の取得に失敗しました: ${errorMessage}`);
    } finally {
      setIsHistoryLoading(false);
    }
  }, []);

  // 現場履歴取得
  const loadSiteHistory = useCallback(async (siteId: number): Promise<void> => {
    setIsHistoryLoading(true);
    setSiteHistory([]);
    setSiteInfo(null);
    try {
      const result = await siteApiService.getSitePanelHistory({ siteId });
      setSiteHistory(result.history);
      setSiteInfo({
        siteName: result.siteName,
        currentPanels: result.currentPanels,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '不明なエラー';
      message.error(`履歴の取得に失敗しました: ${errorMessage}`);
    } finally {
      setIsHistoryLoading(false);
    }
  }, []);

  // 盤選択時
  const handlePanelSelect = (panelId: number): void => {
    setSelectedPanelId(panelId);
    void loadPanelHistory(panelId);
  };

  // 現場選択時
  const handleSiteSelect = (siteId: number): void => {
    setSelectedSiteId(siteId);
    void loadSiteHistory(siteId);
  };

  // モード切替時
  const handleModeChange = (mode: ViewMode): void => {
    setViewMode(mode);
    setSelectedPanelId(null);
    setSelectedSiteId(null);
    setPanelHistory([]);
    setSiteHistory([]);
    setPanelInfo(null);
    setSiteInfo(null);
  };

  // CSVエクスポート（盤履歴）
  const handleExportPanelHistory = (): void => {
    if (panelInfo === null || panelHistory.length === 0) {
      message.warning('エクスポートするデータがありません');
      return;
    }

    const headers = ['現場名', '開始日時', '終了日時', '移動理由', '実行者', '備考'];
    const rows = panelHistory.map((h) => [
      escapeCsvValue(h.siteName),
      escapeCsvValue(formatDateTime(h.startedAt)),
      escapeCsvValue(h.endedAt !== null ? formatDateTime(h.endedAt) : ''),
      escapeCsvValue(h.transferReasonLabel),
      escapeCsvValue(h.performedBy.userName),
      escapeCsvValue(h.note),
    ]);

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    const filename = `盤履歴_${panelInfo.panelName}_${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCsv(csvContent, filename);
    message.success('CSVをダウンロードしました');
  };

  // CSVエクスポート（現場履歴）
  const handleExportSiteHistory = (): void => {
    if (siteInfo === null || siteHistory.length === 0) {
      message.warning('エクスポートするデータがありません');
      return;
    }

    const headers = ['盤名', '開始日時', '終了日時', '移動理由'];
    const rows = siteHistory.map((h) => [
      escapeCsvValue(h.panelName),
      escapeCsvValue(formatDateTime(h.startedAt)),
      escapeCsvValue(h.endedAt !== null ? formatDateTime(h.endedAt) : ''),
      escapeCsvValue(h.transferReasonLabel),
    ]);

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    const filename = `現場履歴_${siteInfo.siteName}_${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCsv(csvContent, filename);
    message.success('CSVをダウンロードしました');
  };

  // 盤履歴テーブルカラム
  const panelHistoryColumns = [
    {
      title: '現場名',
      dataIndex: 'siteName',
      key: 'siteName',
      render: (siteName: string, record: PanelSiteHistoryItem) => (
        <Space>
          <EnvironmentOutlined />
          <span>{siteName}</span>
          {record.endedAt === null && <Tag color="green">現在</Tag>}
        </Space>
      ),
    },
    {
      title: '開始日時',
      dataIndex: 'startedAt',
      key: 'startedAt',
      render: (startedAt: string) => formatDateTime(startedAt),
    },
    {
      title: '終了日時',
      dataIndex: 'endedAt',
      key: 'endedAt',
      render: (endedAt: string | null) => (endedAt !== null ? formatDateTime(endedAt) : '-'),
    },
    {
      title: '移動理由',
      dataIndex: 'transferReasonLabel',
      key: 'transferReasonLabel',
      render: (label: string) => <Tag>{label}</Tag>,
    },
    {
      title: '実行者',
      dataIndex: 'performedBy',
      key: 'performedBy',
      render: (performedBy: { readonly userName: string }) => performedBy.userName,
    },
    {
      title: '備考',
      dataIndex: 'note',
      key: 'note',
      render: (note: string | null) => note ?? '-',
    },
  ];

  // 現場履歴テーブルカラム
  const siteHistoryColumns = [
    {
      title: '盤名',
      dataIndex: 'panelName',
      key: 'panelName',
      render: (panelName: string, record: SitePanelHistoryItem) => (
        <Space>
          <AppstoreOutlined />
          <span>{panelName}</span>
          {record.endedAt === null && <Tag color="green">現在</Tag>}
        </Space>
      ),
    },
    {
      title: '開始日時',
      dataIndex: 'startedAt',
      key: 'startedAt',
      render: (startedAt: string) => formatDateTime(startedAt),
    },
    {
      title: '終了日時',
      dataIndex: 'endedAt',
      key: 'endedAt',
      render: (endedAt: string | null) => (endedAt !== null ? formatDateTime(endedAt) : '-'),
    },
    {
      title: '移動理由',
      dataIndex: 'transferReasonLabel',
      key: 'transferReasonLabel',
      render: (label: string) => <Tag>{label}</Tag>,
    },
  ];

  // モバイル用盤履歴カラム
  const mobilePanelHistoryColumns = [
    {
      title: '現場',
      dataIndex: 'siteName',
      key: 'siteName',
      render: (siteName: string, record: PanelSiteHistoryItem) => (
        <div>
          <div>
            {siteName}
            {record.endedAt === null && <Tag color="green" style={{ marginLeft: 4 }}>現在</Tag>}
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatDateTime(record.startedAt)}
            {record.endedAt !== null && ` 〜 ${formatDateTime(record.endedAt)}`}
          </Text>
        </div>
      ),
    },
    {
      title: '理由',
      dataIndex: 'transferReasonLabel',
      key: 'transferReasonLabel',
      width: 100,
    },
  ];

  // モバイル用現場履歴カラム
  const mobileSiteHistoryColumns = [
    {
      title: '盤',
      dataIndex: 'panelName',
      key: 'panelName',
      render: (panelName: string, record: SitePanelHistoryItem) => (
        <div>
          <div>
            {panelName}
            {record.endedAt === null && <Tag color="green" style={{ marginLeft: 4 }}>現在</Tag>}
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatDateTime(record.startedAt)}
            {record.endedAt !== null && ` 〜 ${formatDateTime(record.endedAt)}`}
          </Text>
        </div>
      ),
    },
    {
      title: '理由',
      dataIndex: 'transferReasonLabel',
      key: 'transferReasonLabel',
      width: 100,
    },
  ];

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Title level={3}>
        <HistoryOutlined style={{ marginRight: 8 }} />
        アーカイブ済みデータ
      </Title>

      <Card style={{ marginBottom: 16 }}>
        <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
          <Select
            value={viewMode}
            onChange={handleModeChange}
            style={{ width: 150 }}
            options={[
              { value: 'panel', label: '盤から検索' },
              { value: 'site', label: '現場から検索' },
            ]}
          />

          {viewMode === 'panel' ? (
            <Select
              placeholder="盤を選択"
              value={selectedPanelId}
              onChange={handlePanelSelect}
              style={{ width: isMobile ? '100%' : 300 }}
              showSearch
              optionFilterProp="label"
              options={panels.map((panel) => {
                const site = sites.find((s) => s.id === panel.siteId);
                const siteName = site?.siteName ?? '不明';
                return {
                  value: panel.id,
                  label: `${panel.panelName}（${siteName}）`,
                };
              })}
            />
          ) : (
            <Select
              placeholder="現場を選択"
              value={selectedSiteId}
              onChange={handleSiteSelect}
              style={{ width: isMobile ? '100%' : 300 }}
              showSearch
              optionFilterProp="label"
              options={sites.map((site) => ({
                value: site.id,
                label: site.siteName,
              }))}
            />
          )}
        </Space>
      </Card>

      {/* 選択情報表示 */}
      {viewMode === 'panel' && panelInfo !== null && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <Space>
              <AppstoreOutlined />
              <Text strong>{panelInfo.panelName}</Text>
              <Text type="secondary">→ 現在の現場:</Text>
              <Tag color="blue">{panelInfo.currentSite.siteName}</Tag>
            </Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportPanelHistory}
              disabled={panelHistory.length === 0}
            >
              CSV出力
            </Button>
          </div>
        </Card>
      )}

      {viewMode === 'site' && siteInfo !== null && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <Space wrap>
              <EnvironmentOutlined />
              <Text strong>{siteInfo.siteName}</Text>
              <Text type="secondary">現在の盤:</Text>
              {siteInfo.currentPanels.length > 0 ? (
                siteInfo.currentPanels.map((panel) => (
                  <Tag key={panel.id} color="blue">{panel.panelName}</Tag>
                ))
              ) : (
                <Text type="secondary">なし</Text>
              )}
            </Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportSiteHistory}
              disabled={siteHistory.length === 0}
            >
              CSV出力
            </Button>
          </div>
        </Card>
      )}

      {/* 履歴テーブル */}
      {isHistoryLoading ? (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin />
        </div>
      ) : viewMode === 'panel' && selectedPanelId !== null ? (
        <Table
          columns={isMobile ? mobilePanelHistoryColumns : panelHistoryColumns}
          dataSource={[...panelHistory]}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: <Empty description="履歴データがありません" /> }}
        />
      ) : viewMode === 'site' && selectedSiteId !== null ? (
        <Table
          columns={isMobile ? mobileSiteHistoryColumns : siteHistoryColumns}
          dataSource={[...siteHistory]}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: <Empty description="履歴データがありません" /> }}
        />
      ) : (
        <Empty description="盤または現場を選択してください" />
      )}
    </div>
  );
};
